/**
 * Admin Screen
 * Main admin interface with navigation between different admin sections
 */

import { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { AdminDashboard } from './admin/AdminDashboard';
import { AdminUsers } from './admin/AdminUsers';
import { AdminBroadcast } from './admin/AdminBroadcast';
import { AdminTasks } from './admin/AdminTasks';
import { AdminWithdrawals } from './admin/AdminWithdrawals';
import { AdminDeposits } from './admin/AdminDeposits';
import { AdminBatteries } from './admin/AdminBatteries';
import { AdminTrades } from './admin/AdminTrades';
import { AdminTransfers } from './admin/AdminTransfers';
import { AdminStaking } from './admin/AdminStaking';
import { AdminAiTrader } from './admin/AdminAiTrader';

type AdminSection = 'dashboard' | 'users' | 'deposits' | 'broadcast' | 'trades' | 'assets' | 'tasks' | 'withdrawals' | 'batteries' | 'transfers' | 'staking' | 'ai-trader';

interface AdminScreenProps {
  onBack: () => void;
}

export function AdminScreen({ onBack }: AdminScreenProps) {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<AdminSection>('dashboard');

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <AdminDashboard onNavigate={setActiveSection} />;
      case 'users':
        return <AdminUsers />;
      case 'broadcast':
        return <AdminBroadcast />;
      case 'tasks':
        return <AdminTasks />;
      case 'withdrawals':
        return <AdminWithdrawals />;
      case 'deposits':
        return <AdminDeposits />;
      case 'batteries':
        return <AdminBatteries />;
      case 'trades':
        return <AdminTrades />;
      case 'transfers':
        return <AdminTransfers />;
      case 'staking':
        return <AdminStaking />;
      case 'ai-trader':
        return <AdminAiTrader />;
      case 'assets':
        return (
          <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚧</div>
              <div style={{ fontSize: '18px' }}>Assets management coming soon</div>
            </div>
          </div>
        );
      default:
        return <AdminDashboard onNavigate={setActiveSection} />;
    }
  };

  const renderNavigation = () => {
    if (activeSection === 'dashboard') {
      return null; // Dashboard has its own navigation
    }

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(10, 22, 40, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
        zIndex: 100,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          gap: '12px',
        }}>
          <button
            onClick={() => setActiveSection('dashboard')}
            style={{
              background: 'rgba(59, 130, 246, 0.2)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '6px 12px',
              minWidth: '40px',
              minHeight: '36px',
            }}
          >
            ←
          </button>
          <h1 style={{
            fontSize: '20px',
            fontWeight: 700,
            margin: 0,
            color: '#fff',
          }}>
            {getSectionTitle()}
          </h1>
          <div style={{ flex: 1 }} />
          <button
            onClick={onBack}
            style={{
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '6px 12px',
              color: '#ef4444',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Exit Admin
          </button>
        </div>
      </div>
    );
  };

  const getSectionTitle = () => {
    switch (activeSection) {
      case 'users': return t('admin.users');
      case 'broadcast': return t('admin.broadcast');
      case 'tasks': return t('admin.tasks');
      case 'withdrawals': return 'Withdrawals';
      case 'deposits': return 'Deposits';
      case 'batteries': return 'Batteries';
      case 'trades': return 'Trades';
      case 'transfers': return 'Transfers';
      case 'staking': return 'Staking';
      case 'ai-trader': return 'AI Trader';
      case 'assets': return 'Assets';
      default: return t('admin.dashboard');
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {renderNavigation()}
      <div style={{ paddingTop: activeSection !== 'dashboard' ? '70px' : '0' }}>
        {renderSection()}
      </div>
      
      {/* Back to Dashboard Button (for non-dashboard sections) */}
      {activeSection !== 'dashboard' && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 100,
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
              border: 'none',
              borderRadius: '50px',
              padding: '12px 20px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>🏠</span>
            Exit Admin
          </button>
        </div>
      )}
    </div>
  );
}